/* eslint-disable import/no-unused-modules */
import { Image } from '@rocco/components/image';
import type { Currency, ProductItem } from '../../type/types';
import { proposalProductColumnsClass } from '../../constants';
import { proposalFormatPrice } from '../../util/utils';
import { cn } from '@rocco/index';

interface ProductRowProps {
    productItem: ProductItem;
    currency: Currency;
    isMobile: boolean;
}

const TYPO_CLASS = {
    PRODUCT_NAME: 'font-medium text-[#141414] md:text-base',
    PRODUCT_DESCRIPTION: 'text-[0.69rem] text-[#6E6E6E] md:text-[0.80rem]',
    PRODUCT_QUANTITY:
        'font-display md:font-sans font-medium md:font-normal md:text-[0.66rem] tracking-[-0.01em] md:tracking-normal text-[#9B9B9B] md:text-[#6E6E6E]',
    PRODUCT_UNIT_PRICE:
        'font-display font-medium md:font-[450] md:text-[0.86rem] tracking-[-0.01em] text-[#9B9B9B] md:text-[#7E7E7E]',
    // for mobile only
    COLUMN_HEADER:
        'font-medium text-[0.54rem] tracking-[-0.01em] text-[#9B9B9B]',
    PRICE: 'font-display font-semibold tracking-[-0.01em] text-[#141414] md:text-base',
};

/**
 * Product row component for displaying product information in a grid layout
 */
export const ProductRow = ({
    productItem,
    currency,
    isMobile,
}: ProductRowProps) => {
    const divClass = 'rounded-md bg-[#f6f6f6] backdrop-blur-[30px]';
    const ImageComponent = (
        <>
            {productItem.imageUrl ? (
                <Image
                    src={productItem.imageUrl}
                    alt={productItem.name}
                    className="h-15 w-15 rounded-md"
                />
            ) : null}
        </>
    );
    const ProductNameComponent = (
        <div className="flex flex-col">
            <span className={cn(TYPO_CLASS.PRODUCT_NAME, 'mt-4')}>
                {productItem.name}
            </span>
            <span
                className={cn(
                    TYPO_CLASS.PRODUCT_DESCRIPTION,
                    'mb-4 max-w-70 whitespace-pre-line'
                )}
            >
                {productItem.description}
            </span>
        </div>
    );

    if (isMobile) {
        return (
            <div className={cn(divClass, 'p-3')}>
                {/* First row: Product info with description */}
                <div className="mb-2 flex flex-row gap-2">
                    {ImageComponent}
                    {ProductNameComponent}
                </div>

                {/* Second row: Qty, Unit Price, Price */}
                <div className="flex flex-row items-center">
                    <div className="flex w-[36%] flex-col">
                        <span
                            className={cn(
                                TYPO_CLASS.COLUMN_HEADER,
                                'mb-1 uppercase'
                            )}
                        >
                            Qty
                        </span>
                        <span className={cn(TYPO_CLASS.PRODUCT_QUANTITY)}>
                            {productItem.quantity}
                        </span>
                    </div>
                    <div className="flex w-[32%] flex-col text-right">
                        <span
                            className={cn(
                                TYPO_CLASS.COLUMN_HEADER,
                                'mb-1 uppercase'
                            )}
                        >
                            Unit Price
                        </span>
                        <span className={cn(TYPO_CLASS.PRODUCT_UNIT_PRICE)}>
                            {proposalFormatPrice(
                                productItem.unitPrice,
                                currency
                            )}
                        </span>
                    </div>
                    <div className="flex w-[32%] flex-col text-right">
                        <span
                            className={cn(
                                TYPO_CLASS.COLUMN_HEADER,
                                'mb-1 uppercase'
                            )}
                        >
                            Price
                        </span>
                        <span className={cn(TYPO_CLASS.PRICE)}>
                            {proposalFormatPrice(
                                productItem.totalPrice,
                                currency
                            )}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={cn('grid', proposalProductColumnsClass, divClass)}>
            <div className="flex flex-row gap-2 p-2">
                {ImageComponent}
                {ProductNameComponent}
            </div>
            <div className={cn(TYPO_CLASS.PRODUCT_QUANTITY, 'mt-8')}>
                {productItem.quantity}
            </div>
            <div
                className={cn(
                    TYPO_CLASS.PRODUCT_UNIT_PRICE,
                    'mt-8 print:text-base'
                )}
            >
                <span>
                    {proposalFormatPrice(productItem.unitPrice, currency)}
                </span>
            </div>
            <div
                className={cn(
                    TYPO_CLASS.PRODUCT_UNIT_PRICE,
                    'mt-8 print:text-base'
                )}
            >
                {proposalFormatPrice(productItem.discountedUnitPrice, currency)}
            </div>
            <div
                className={cn(
                    TYPO_CLASS.PRODUCT_UNIT_PRICE,
                    'mt-8 print:text-base'
                )}
            >
                {(productItem.taxRate * 100).toFixed(0)}%
            </div>
            <div
                className={cn(
                    TYPO_CLASS.PRICE,
                    'mr-2 mt-8 text-right print:text-base'
                )}
            >
                {proposalFormatPrice(productItem.totalPrice, currency)}
            </div>
        </div>
    );
};

export default ProductRow;
